'use client'

import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { NestedFigsThemeProvider, isUppercase } from '@syconium/little-miss-figgy'
import { TypeStyle, getTypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/themes/default/breakpoints'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { StyledTextFragmentFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useContentfulPageSectionContext } from '../ContentfulPageSectionProvider.client'

import { ContentfulStyledLink } from './ContentfulStyledLink'

export const styledTextQueryFragment = gql(`
  fragment StyledTextFragment on StyledText {
    __typename
    id
    title
    titleTextSize
    paragraph
    paragraphTextSize
    eyebrow
    textColorMobile
    textColorDesktop
    textContentMaxWidthMobile
    textContentMaxWidthDesktop
    textAlignMobile
    textAlignDesktop
    bottomMargin
    links {
      ...StyledLinkFragment
    }
  }
`)

type StyledTextElement = 'eyebrow' | 'title' | 'paragraph' | 'custom' | 'links'

type ContentfulStyledTextProps = StyledTextFragmentFragment & {
  defaultMaxWidth?: number
  defaultBottomMargin?: number
  additionalCustomContent?: React.ReactNode
  centerFloatContent?: boolean
}

const StyledTextOverlay = styled.div<{
  $textContentMaxWidthDesktop: NonNullable<ContentfulStyledTextProps['textContentMaxWidthDesktop']>
  $textContentMaxWidthMobile: NonNullable<ContentfulStyledTextProps['textContentMaxWidthMobile']>
  $textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  $textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
  $bottomMargin: NonNullable<ContentfulStyledTextProps['bottomMargin']>
  $centerFloatContent: boolean
}>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  letter-spacing: 0.6px;
  margin-bottom: ${o => o.$bottomMargin}px;
  text-align: ${o => o.$textAlignMobile || 'center'};

  .configurable-width {
    margin-left: auto;
    margin-right: auto;
  }

  ${untilMd} {
    .configurable-width {
      max-width: ${o => o.$textContentMaxWidthMobile}px;
    }

    > *,
    .configurable-width {
      ${o =>
        o.$centerFloatContent || o.$textAlignMobile !== 'left'
          ? 'margin-left: auto'
          : 'margin-left: 0px'};
      ${o =>
        o.$centerFloatContent || o.$textAlignMobile !== 'right'
          ? 'margin-right: auto'
          : 'margin-right: 0px'};
    }
  }

  ${fromMd} {
    line-height: 170%;
    text-align: ${o => o.$textAlignDesktop || 'center'};

    .configurable-width {
      max-width: ${o => o.$textContentMaxWidthDesktop}px;
    }

    > *,
    .configurable-width {
      ${o =>
        o.$centerFloatContent || o.$textAlignDesktop !== 'left'
          ? 'margin-left: auto'
          : 'margin-left: 0px'};
      ${o =>
        o.$centerFloatContent || o.$textAlignDesktop !== 'right'
          ? 'margin-right: auto'
          : 'margin-right: 0px'};
    }
  }
`

type ContentfulStyledTextTitleProps = {
  className: string
  children?: React.ReactNode
  isUppercase: boolean
  textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
  titleTextSize: ContentfulStyledTextProps['titleTextSize']
  includeBottomMargin: boolean
}

export const ContentfulStyledTextTitle = styled(
  ({ className, children, isUppercase, titleTextSize }: ContentfulStyledTextTitleProps) => {
    const typeStylesMap: Record<
      ContentfulStyledTextProps['titleTextSize'],
      ReturnType<typeof getTypeStyle>
    > = {
      h1: getTypeStyle('headlineExtraLarge'),
      h2: getTypeStyle('headlineLarge'),
      h3: getTypeStyle('headlineMedium'),
      h4: getTypeStyle('headlineSmall'),
    } as const
    const TypeStyleSelection = typeStylesMap[titleTextSize ?? 'h3']
    return (
      <TypeStyleSelection
        color='primary'
        asTag={titleTextSize}
        className={className}
        isUppercase={isUppercase}
      >
        {children}
      </TypeStyleSelection>
    )
  }
)`
  ${o => (o.includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  text-align: ${o => o.textAlignMobile || 'center'};

  ${fromMd} {
    display: block;
    text-align: ${o => o.textAlignDesktop || 'center'};
    ${o => (o.includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
`

const StyledTextEyebrow = styled(TypeStyle.HeadlineExtraSmall)<{ $includeBottomMargin: boolean }>`
  font-weight: bold;
  letter-spacing: 0.15em;
  line-height: 1.25em;
  ${o => (o.$includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  text-transform: uppercase;

  ${fromMd} {
    ${o => (o.$includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
`

const StyledTextLinks = styled.div<{
  $textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  $textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
}>`
  margin-top: ${o => o.theme.spacing(6)};
  margin-bottom: ${o => o.theme.spacing(6)};
  align-items: center;

  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(6)};

  text-align: ${({ $textAlignMobile }) => $textAlignMobile};

  justify-content: ${({ $textAlignMobile }) =>
    $textAlignMobile === 'left'
      ? 'flex-start'
      : $textAlignMobile === 'center'
      ? 'center'
      : $textAlignMobile === 'right'
      ? 'flex-end'
      : 'inherit'};

  ${fromMd} {
    text-align: ${({ $textAlignDesktop }) => $textAlignDesktop};

    justify-content: ${({ $textAlignDesktop }) =>
      $textAlignDesktop === 'left'
        ? 'flex-start'
        : $textAlignDesktop === 'center'
        ? 'center'
        : $textAlignDesktop === 'right'
        ? 'flex-end'
        : 'inherit'};
  }
`
const StyledContentfulStyledLink = styled(ContentfulStyledLink)`
  display: inline-flex;
  align-items: center;
  height: min-content;
`

const StyledTextParagraph = styled(TypeStyle.BodyDefault)<{
  $paragraphTextSize: ContentfulStyledTextProps['paragraphTextSize']
  $includeBottomMargin: boolean
}>`
  ${o => (o.$includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  ${fromMd} {
    ${o => (o.$includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
  ${o => TypeStyle.css[o.$paragraphTextSize]}
`

export const ContentfulStyledText = ({
  defaultMaxWidth = 9999,
  defaultBottomMargin = 0,
  title,
  titleTextSize,
  paragraph,
  paragraphTextSize,
  eyebrow,
  textColorMobile,
  textColorDesktop,
  textContentMaxWidthMobile,
  textContentMaxWidthDesktop,
  textAlignMobile,
  textAlignDesktop,
  bottomMargin,
  links,
  additionalCustomContent,
  centerFloatContent,
}: ContentfulStyledTextProps) => {
  const sectionContext = useContentfulPageSectionContext()

  const includeBottomMargin = useCallback(
    (element: StyledTextElement) => {
      const includedElements: StyledTextElement[] = []
      if (eyebrow) includedElements.push('eyebrow')
      if (title) includedElements.push('title')
      if (paragraph) includedElements.push('paragraph')
      if (additionalCustomContent) includedElements.push('custom')
      if (links.length > 0) includedElements.push('links')
      const indexOfElement = includedElements.indexOf(element)
      return indexOfElement > -1 && indexOfElement < includedElements.length - 1
    },
    [additionalCustomContent, eyebrow, links.length, paragraph, title]
  )

  // Derive desktop/mobile theme from `textColorDesktop` and `textColorMobile`
  const { themeDesktop, themeMobile } = useMemo<{
    themeDesktop: 'light' | 'dark' | 'default'
    themeMobile: 'light' | 'dark' | 'default'
  }>(() => {
    const themeDesktop =
      textColorDesktop === 'light' ? 'dark' : textColorDesktop === 'dark' ? 'light' : 'default'
    const themeMobile =
      textColorMobile === 'light' ? 'dark' : textColorMobile === 'dark' ? 'light' : 'default'

    return { themeDesktop, themeMobile }
  }, [textColorDesktop, textColorMobile])

  return (
    <StyledTextOverlay
      $textContentMaxWidthDesktop={textContentMaxWidthDesktop ?? defaultMaxWidth}
      $textContentMaxWidthMobile={textContentMaxWidthMobile ?? defaultMaxWidth}
      $textAlignMobile={textAlignMobile}
      $textAlignDesktop={textAlignDesktop}
      $bottomMargin={bottomMargin ?? defaultBottomMargin}
      $centerFloatContent={centerFloatContent ?? false}
    >
      <NestedFigsThemeProvider themeMobile={themeMobile} themeDesktop={themeDesktop}>
        {eyebrow && (
          <StyledTextEyebrow
            color='primary'
            $includeBottomMargin={includeBottomMargin('eyebrow')}
            className='configurable-width'
            asTag='h3'
          >
            {eyebrow}
          </StyledTextEyebrow>
        )}

        {title && (
          <ContentfulStyledTextTitle
            titleTextSize={titleTextSize}
            className='configurable-width'
            isUppercase={isUppercase(title)}
            textAlignMobile={textAlignMobile}
            textAlignDesktop={textAlignDesktop}
            includeBottomMargin={includeBottomMargin('title')}
          >
            {title}
          </ContentfulStyledTextTitle>
        )}

        {paragraph && (
          <StyledTextParagraph
            color='primary'
            $includeBottomMargin={includeBottomMargin('paragraph')}
            $paragraphTextSize={paragraphTextSize ?? 'bodyDefault'}
            className='configurable-width'
            asTag='p'
          >
            {paragraph}
          </StyledTextParagraph>
        )}

        {additionalCustomContent}

        {links.length > 0 && (
          <StyledTextLinks
            $textAlignMobile={textAlignMobile}
            $textAlignDesktop={textAlignDesktop}
            className='configurable-width'
          >
            {links.map((link, index) => (
              <StyledContentfulStyledLink
                {...link}
                key={index}
                analyticsContext={{
                  'data-heap-category': sectionContext.pageSectionAnalyticsName,
                  'data-heap-action': `click cta ${index}`,
                  'data-heap-label': link.text,
                  'data-heap-value': link.url,
                }}
                $isUppercase={isUppercase(link.text)}
              />
            ))}
          </StyledTextLinks>
        )}
      </NestedFigsThemeProvider>
    </StyledTextOverlay>
  )
}
